/**
 * Admin Users API
 * ადმინისტრატორის მომხმარებლების API
 */

import { get, put, post } from '@aiia/portal-core';

export interface UserAdminListResponse {
  id: string;
  email: string;
  displayName: string;
  entityType: string | null;
  status: string;
  subscriptionStatus: string | null;
  planCode: string | null;
  planName: string | null;
  widgetCount: number;
  apiKeyCount: number;
  dataFileCount: number;
  storageUsedBytes: number;
  createdAt: string;
  lastActiveAt: string | null;
}

// Note: SubscriptionAdminListResponse is defined in adminListing.ts
interface UserSubscriptionInfo {
  id: string;
  userId: string;
  planCode: string | null;
  planName: string | null;
  status: string;
  billingCycle: string | null;
  discountPercent: number | null;
  discountEndsAt: string | null;
  currentPeriodEnd: string | null;
}

export interface FeatureOverrideResponse {
  id: string;
  userId: string;
  userEmail: string | null;
  featureCode: string;
  featureDisplayName: string;
  customValue: number | null;
  isUnlimited: boolean;
  planValue: number | null;
  planIsUnlimited: boolean;
  reason: string | null;
  createdBy: string | null;
  createdByName: string | null;
  expiresAt: string | null;
  isExpired: boolean;
  createdAt: string;
  updatedAt: string | null;
}

export interface UserDetailAdminResponse {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  companyName: string | null;
  companyId: string | null;
  entityType: string | null;
  status: string;
  createdAt: string;
  updatedAt: string | null;
  deletedAt: string | null;
  deletedBy: string | null;
  subscription: UserSubscriptionInfo | null;
  widgetCount: number;
  apiKeyCount: number;
  dataFileCount: number;
  storageUsedBytes: number;
  promptCount: number;
  toolCount: number;
  effectiveLimits: Record<string, number>;
  featureOverrides: FeatureOverrideResponse[];
}

export interface ChangeUserPlanRequest {
  newPlanCode: string;
  immediate?: boolean;
  reason?: string;
}

export interface ApplyDiscountRequest {
  discountPercent: number;
  durationMonths?: number;
  reason?: string;
}

export interface SuspendUserRequest {
  reason: string;
}

// PagedResponse is defined in adminListing.ts
import type { PagedResponse } from './adminListing';

export interface AdminUsersParams {
  search?: string;
  status?: string;
  planCode?: string;
  page?: number;
  size?: number;
}

// Get users list with pagination
export const getAdminUsers = (params: AdminUsersParams = {}) => {
  const queryParams = new URLSearchParams();
  if (params.search) queryParams.append('search', params.search);
  if (params.status) queryParams.append('status', params.status);
  if (params.planCode) queryParams.append('planCode', params.planCode);
  if (params.page !== undefined) queryParams.append('page', params.page.toString());
  if (params.size !== undefined) queryParams.append('size', params.size.toString());

  const queryString = queryParams.toString();
  return get<PagedResponse<UserAdminListResponse>>(`/admin/users${queryString ? `?${queryString}` : ''}`);
};

// Get user details
export const getAdminUserDetails = (userId: string) =>
  get<UserDetailAdminResponse>(`/admin/users/${userId}`);

// Change user's plan
export const changeUserPlan = (userId: string, data: ChangeUserPlanRequest) =>
  put<void>(`/admin/users/${userId}/plan`, data);

// Apply discount to user
export const applyUserDiscount = (userId: string, data: ApplyDiscountRequest) =>
  put<void>(`/admin/users/${userId}/discount`, data);

// Suspend user
export const suspendUser = (userId: string, data: SuspendUserRequest) =>
  put<void>(`/admin/users/${userId}/suspend`, data);

// Activate user
export const activateUser = (userId: string) =>
  put<void>(`/admin/users/${userId}/activate`, {});

// Create custom invoice for user (admin)
export interface CreateCustomInvoiceRequest {
  targetPlanCode: string;
  amount: number;
  currency: string;
  billingCycle: string;
  notes?: string;
}

export interface CreateCustomInvoiceResponse {
  invoiceId: string;
  invoiceNumber: string;
  paymentUrl: string;
  amount: number;
  currency: string;
}

export const createCustomInvoice = (userId: string, data: CreateCustomInvoiceRequest) =>
  post<CreateCustomInvoiceResponse>(`/admin/subscriptions/${userId}/create-invoice`, data);

// Admin per-user analytics (reuses same response types as user analytics)
import type { AnalyticsOverviewResponse, AnalyticsTrafficResponse, StatisticsPeriod } from './analytics';

export const getAdminUserAnalyticsOverview = (userId: string) =>
  get<AnalyticsOverviewResponse>(`/admin/users/${userId}/analytics/overview`);

export const getAdminUserAnalyticsTraffic = (userId: string, period: StatisticsPeriod = 'MONTH') =>
  get<AnalyticsTrafficResponse>(`/admin/users/${userId}/analytics/traffic?period=${period}`);
